import React, { useState, useEffect, useRef } from "react";
import { ArrowRight } from "lucide-react";

const SCENARIOS = [
  {
    tab: "📅 Agendar cita",
    messages: [
      { from: "patient", text: "Hola, quisiera agendar una cita" },
      { from: "sofi",    text: "¡Hola! Con gusto te ayudo 😊 ¿Para qué especialidad necesitas la cita?" },
      { from: "patient", text: "Medicina general" },
      { from: "sofi",    text: "Tenemos disponibilidad este martes a las 10am o jueves a las 4pm. ¿Cuál te viene mejor?" },
      { from: "patient", text: "El jueves a las 4" },
      { from: "sofi",    text: "✅ ¡Listo! Tu cita quedó agendada para el jueves a las 4:00pm." },
    ],
  },
  // TEN.4: aqui habia una pestana donde Sofi REAGENDABA una cita. Sofi no reagenda
  // (auditoria #2): se retira la pestana entera, no se reescribe.
  {
    tab: "💰 Consultar precio",
    messages: [
      { from: "patient", text: "¿Cuánto cuesta la consulta general?" },
      { from: "sofi",    text: "La consulta de medicina general tiene un costo de $450 MXN." },
      { from: "patient", text: "¿Incluye revisión?" },
      { from: "sofi",    text: "Sí, incluye exploración física completa y receta médica si se requiere." },
      { from: "patient", text: "Qué bien, me anoto" },
      { from: "sofi",    text: "¡Perfecto! ¿Quieres que te agende una cita ahora? Tenemos lugar esta semana 😊" },
    ],
  },
];

function delay(ms) {
  return new Promise((res) => setTimeout(res, ms));
}

const BENEFITS = [
  // J1-B (17 Ago 2026): este beneficio prometia una latencia concreta en segundos. Nunca se
  // midio -- era una cifra puesta a ojo. Se deja cualitativo, en linea con el resto del copy.
  // WEB.CL2 (11 Oct 2026): sin promesas de tiempo de respuesta ni de cero intervención.
  "Responde las dudas frecuentes con la información de tu clínica",
  "Agenda en los horarios libres de cada profesional",
  "Pacientes atendidos aunque la clínica esté cerrada",
];

export default function InteractiveDemo() {
  const [activeTab, setActiveTab] = useState(0);
  const [visibleMessages, setVisibleMessages] = useState([]);
  const [showTyping, setShowTyping] = useState(false);
  const chatRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setVisibleMessages([]);
    setShowTyping(false);

    async function run() {
      await delay(300);
      if (cancelled) return;

      const msgs = SCENARIOS[activeTab].messages;
      for (let i = 0; i < msgs.length; i++) {
        if (cancelled) return;

        if (msgs[i].from === "sofi") {
          setShowTyping(true);
          await delay(700);
          if (cancelled) return;
          setShowTyping(false);
        }

        setVisibleMessages((prev) => [...prev, msgs[i]]);

        if (i < msgs.length - 1) {
          await delay(900);
        }
      }
    }

    run();
    return () => { cancelled = true; };
  }, [activeTab]);

  useEffect(() => {
    chatRef.current?.scrollTo({ top: chatRef.current.scrollHeight, behavior: "smooth" });
  }, [visibleMessages, showTyping]);

  return (
    <section className="py-24 sm:py-28 bg-white" id="demo">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="max-w-2xl mb-12">
          <p
            style={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.18em",
              color: "#2563EB",
              textTransform: "uppercase",
              marginBottom: 10,
            }}
          >
            CONVERSACIÓN DE EJEMPLO
          </p>
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">
            Así atiende Sofi a tus pacientes
          </h2>
          <p className="text-slate-600 mt-4 text-lg leading-relaxed">
            Responde y agenda también fuera de tu horario.
          </p>
        </div>

        {/* 2-col layout */}
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          {/* Left col */}
          <div className="flex flex-col gap-8">
            {/* Benefits */}
            <div className="space-y-4">
              {BENEFITS.map((item, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div
                    style={{
                      width: 22,
                      height: 22,
                      borderRadius: 9999,
                      background: "#D1FAE5",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      marginTop: 1,
                    }}
                  >
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <path
                        d="M2 6l3 3 5-5"
                        stroke="#059669"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <span className="text-slate-600 text-sm leading-relaxed">{item}</span>
                </div>
              ))}
            </div>

            {/* CTA */}
            <div>
              <a
                href="/demo"
                className="inline-flex items-center gap-2 font-medium text-white transition-colors"
                style={{ backgroundColor: "#2563EB", borderRadius: 10, padding: "12px 22px", fontSize: 15 }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#1D4ED8")}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#2563EB")}
              >
                Ver la demo <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Right col — tabs + mockup */}
          <div>
            {/* Scenario tabs */}
            <div className="flex flex-wrap gap-2 mb-5">
              {SCENARIOS.map((s, i) => (
                <button
                  key={i}
                  onClick={() => setActiveTab(i)}
                  style={{
                    padding: "8px 14px",
                    borderRadius: 8,
                    fontSize: 13,
                    fontWeight: 600,
                    border: "1px solid",
                    cursor: "pointer",
                    transition: "all 0.15s",
                    borderColor: activeTab === i ? "#2563EB" : "#E2E8F0",
                    background: activeTab === i ? "#DBE5FF" : "#FFFFFF",
                    color: activeTab === i ? "#1D4ED8" : "#64748B",
                  }}
                >
                  {s.tab}
                </button>
              ))}
            </div>

            {/* WhatsApp mockup */}
            <div
              style={{
                borderRadius: 20,
                overflow: "hidden",
                boxShadow: "0 30px 60px -30px rgba(15,23,42,.25), 0 8px 24px -12px rgba(15,23,42,.16)",
                maxWidth: 380,
              }}
            >
              {/* WA Header */}
              <div
                style={{
                  background: "#075E54",
                  padding: "12px 16px",
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                }}
              >
                <div
                  style={{
                    width: 38,
                    height: 38,
                    borderRadius: 9999,
                    background: "#25D366",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <span style={{ color: "#FFFFFF", fontWeight: 700, fontSize: 16 }}>S</span>
                </div>
                <div>
                  <div style={{ color: "#FFFFFF", fontWeight: 600, fontSize: 14, lineHeight: 1.2 }}>
                    Sofi · Clínica
                  </div>
                  <div
                    style={{
                      color: "#9DE1B6",
                      fontSize: 12,
                      display: "flex",
                      alignItems: "center",
                      gap: 4,
                      marginTop: 2,
                    }}
                  >
                    <span
                      style={{
                        width: 7,
                        height: 7,
                        borderRadius: 9999,
                        background: "#4ADE80",
                        display: "inline-block",
                      }}
                    />
                    En línea
                  </div>
                </div>
              </div>

              {/* Messages area */}
              <div
                ref={chatRef}
                style={{
                  background: "#ECE5DD",
                  padding: "14px 12px",
                  minHeight: 340,
                  maxHeight: 340,
                  overflowY: "auto",
                  display: "flex",
                  flexDirection: "column",
                  gap: 7,
                }}
              >
                {visibleMessages.map((msg, i) => (
                  <div
                    key={i}
                    style={{
                      display: "flex",
                      justifyContent: msg.from === "patient" ? "flex-end" : "flex-start",
                    }}
                  >
                    {msg.from === "patient" ? (
                      <div
                        style={{
                          background: "#DCF8C6",
                          color: "#111B21",
                          borderRadius: "18px 18px 4px 18px",
                          padding: "8px 12px",
                          maxWidth: "78%",
                          fontSize: 13.5,
                          lineHeight: 1.45,
                          boxShadow: "0 1px 0.5px rgba(0,0,0,.13)",
                        }}
                      >
                        {msg.text}
                        <span
                          style={{
                            color: "#53BDEB",
                            fontSize: 11,
                            marginLeft: 6,
                            verticalAlign: "middle",
                            letterSpacing: "-1px",
                          }}
                        >
                          ✓✓
                        </span>
                      </div>
                    ) : (
                      <div
                        style={{
                          background: "#FFFFFF",
                          color: "#111B21",
                          borderRadius: "18px 18px 18px 4px",
                          padding: "8px 12px",
                          maxWidth: "78%",
                          fontSize: 13.5,
                          lineHeight: 1.45,
                          boxShadow: "0 1px 0.5px rgba(0,0,0,.13)",
                        }}
                      >
                        {msg.text}
                      </div>
                    )}
                  </div>
                ))}

                {showTyping && (
                  <div style={{ display: "flex", justifyContent: "flex-start" }}>
                    <div
                      style={{
                        background: "#FFFFFF",
                        borderRadius: "18px 18px 18px 4px",
                        padding: "10px 14px",
                        boxShadow: "0 1px 0.5px rgba(0,0,0,.13)",
                        display: "flex",
                        gap: 4,
                        alignItems: "center",
                      }}
                    >
                      {[0, 1, 2].map((j) => (
                        <span
                          key={j}
                          className="animate-bounce"
                          style={{
                            width: 7,
                            height: 7,
                            borderRadius: 9999,
                            background: "#9CA3AF",
                            display: "inline-block",
                            animationDelay: `${j * 0.15}s`,
                          }}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Input bar */}
              <div
                style={{
                  background: "#F0F0F0",
                  padding: "8px 12px",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <div
                  style={{
                    flex: 1,
                    background: "#FFFFFF",
                    borderRadius: 20,
                    padding: "8px 14px",
                    fontSize: 13,
                    color: "#AAAAAA",
                  }}
                >
                  Escribe un mensaje…
                </div>
                <div
                  style={{
                    width: 34,
                    height: 34,
                    borderRadius: 9999,
                    background: "#25D366",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
                    <path d="M2 21l21-9L2 3v7l15 2-15 2z" />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
